import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';

export enum PublishToSalesforceDialogErrorTypes {
  fetchCredentials = 'Error retrieving OAuth credentials.',
  getSalesforceApps = 'Error retrieving Tableau CRM apps.',
  publishToSalesforce = 'Error publishing to Salesforce.',
  exceedsViewsSelectedLimit = 'Number of views selected cannot exceed 25'
}

export enum PublishToSalesforceDialogErrorMessages {
  exceedsViewsError = 'No more than 25 views can be  published at once'
}

export enum PublishToSalesforceTelemetryEvents {
  ExistingCredentialsDialog = 'Publish_Existing_Credentials_Dialog',
  NoCredentialsDialog = 'Publish_No_Credentials_Dialog',
  NavigateToSalesforce = 'Navigate_To_Salesforce',
  PublishButtonClicked = 'Publish_Dialog_Publish_Completed',
  DialogErrors = 'Publish_Dialog_Errors'
}

export interface CloseDialogFlowParams {
  is_existing_credentials_dialog?: boolean;
  no_credentials_dialog_cancelled: boolean;
  existing_credentials_dialog_cancelled: boolean;
  clicked_publish?: boolean;
  selected_app_id?: string;
  existing_credential_clicked?: boolean;
  new_credential_added: boolean;
  num_apps_returned?: number;
  num_views_selected: number;
  site_luid: string;
  user_id: string;
  num_times_more_apps_loaded: number;
  apps_had_next_page: boolean;
}

export const getInitialCloseDialogTelemetry = (): CloseDialogFlowParams => {
  return {
    is_existing_credentials_dialog: false,
    no_credentials_dialog_cancelled: true,
    existing_credentials_dialog_cancelled: true,
    clicked_publish: false,
    selected_app_id: '',
    existing_credential_clicked: false,
    new_credential_added: false,
    num_apps_returned: 0,
    num_views_selected: 0,
    site_luid: '',
    user_id: '',
    num_times_more_apps_loaded: 0,
    apps_had_next_page: false,
  };
};

export namespace PublishToSalesforceTelemetryParams {
  export let closePublishDialogParams: CloseDialogFlowParams = getInitialCloseDialogTelemetry();
}

export interface ExistingCredentialsDialogEvent extends ValidTelemetryEvent {
  type: PublishToSalesforceTelemetryEvents.ExistingCredentialsDialog;
  dialog_cancelled: boolean;
  selected_app_id: string;
  existing_credential_clicked: boolean;
  new_credential_added: boolean;
  num_apps_returned: number;
  num_views_selected: number;
  site_luid: string;
  user_id: string;
  num_times_more_apps_loaded: number;
  apps_had_next_page: boolean;
}

export function sendExistingCredentialsDialogTelemetry(params: CloseDialogFlowParams) {
  Telemetry.sendEvent<ExistingCredentialsDialogEvent>({
    type: PublishToSalesforceTelemetryEvents.ExistingCredentialsDialog,
    dialog_cancelled: params.existing_credentials_dialog_cancelled,
    selected_app_id: params.selected_app_id,
    existing_credential_clicked: params.existing_credential_clicked,
    new_credential_added: params.new_credential_added,
    num_apps_returned: params.num_apps_returned,
    num_views_selected: params.num_views_selected,
    site_luid: params.site_luid,
    user_id: params.user_id,
    num_times_more_apps_loaded: params.num_times_more_apps_loaded,
    apps_had_next_page: params.apps_had_next_page,
  });
}

export interface NoCredentialsDialogEvent extends ValidTelemetryEvent {
  type: PublishToSalesforceTelemetryEvents.NoCredentialsDialog;
  new_credential_added: boolean;
  dialog_cancelled: boolean;
  num_views_selected: number;
  site_luid: string;
  user_id: string;
}

export function sendNoCredentialsDialogTelemetry(params: CloseDialogFlowParams) {
  Telemetry.sendEvent<NoCredentialsDialogEvent>({
    type: PublishToSalesforceTelemetryEvents.NoCredentialsDialog,
    new_credential_added: params.new_credential_added,
    dialog_cancelled: params.no_credentials_dialog_cancelled,
    num_views_selected: params.num_views_selected,
    site_luid: params.site_luid,
    user_id: params.user_id,
  });
}

export interface NavigateToSalesforceEvent extends ValidTelemetryEvent {
  type: PublishToSalesforceTelemetryEvents.NavigateToSalesforce;
  site_luid: string;
  user_id: string;
}

export function sendNavigateToSalesforceTelemetry(site_luid: string, user_id: string) {
  Telemetry.sendEvent<NavigateToSalesforceEvent>({
    type: PublishToSalesforceTelemetryEvents.NavigateToSalesforce,
    site_luid: site_luid,
    user_id: user_id,
  });
}

export interface PublishCompletedParams {
  num_successes: number;
  num_failures: number;
  view_luids: string;
  destination_app_id: string;
  session_management_service: SessionManagementService;
}

export interface PublishCompletedEvent extends ValidTelemetryEvent {
  type: PublishToSalesforceTelemetryEvents.PublishButtonClicked;
  num_successes: number;
  num_failures: number;
  view_luids: string;
  destination_app_id: string;
  site_luid: string;
  user_id: string;
}

export function sendPublishCompletedTelemetry(params: PublishCompletedParams) {
  Telemetry.sendEvent<PublishCompletedEvent>({
    type: PublishToSalesforceTelemetryEvents.PublishButtonClicked,
    num_successes: params.num_successes,
    num_failures: params.num_failures,
    view_luids: params.view_luids,
    destination_app_id: params.destination_app_id,
    site_luid: params.session_management_service.sessionSiteLuid(),
    user_id: params.session_management_service.sessionUserId(),
  });
}


export interface DialogErrorsEvent extends ValidTelemetryEvent {
  type: PublishToSalesforceTelemetryEvents.DialogErrors;
  error_type: PublishToSalesforceDialogErrorTypes;
  error_message: string;
  site_luid: string;
  user_id: string;
}

export function sendDialogErrorsTelemetry(sessionService: SessionManagementService, errorType: PublishToSalesforceDialogErrorTypes, errorMessage: string) {
  Telemetry.sendEvent<DialogErrorsEvent>({
    type: PublishToSalesforceTelemetryEvents.DialogErrors,
    error_message: errorMessage,
    error_type: errorType,
    site_luid: sessionService.sessionSiteLuid(),
    user_id: sessionService.sessionUserId(),
  });
}
